// The font menu: curated faces pinned on top, any Google Font reachable below —
// the color-picker pattern (palette swatches + a full picker) applied to type.
// Sections: the BRAND's recorded faces, the engine's CURATED registers (each
// previewed in its own face, loaded on open via the same registry-mirrored
// ladder the master uses), the built-in trio, then a free search: type any
// Google Fonts family and it's validated by an ACTUAL ladder load before it can
// be applied — a family the master can't load can never be picked.

import { Check, ChevronDown, Loader2, Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { LOGICAL_FONT_FAMILIES, probeFamily } from '../use-composition-fonts'

// The engine's own taste, not a 1,500-font firehose: the voice-registry displays
// plus a few classics across registers (condensed / editorial serif / grotesk /
// rounded / handwritten). Extending this list is a taste decision, not plumbing.
export const CURATED_FAMILIES = [
  'Archivo Black',
  'Bebas Neue',
  'Oswald',
  'Space Grotesk',
  'Playfair Display',
  'DM Serif Display',
  'Lora',
  'Nunito',
  'Poppins',
  'Montserrat',
  'Caveat',
  'Permanent Marker',
]

const BUILTIN = [
  { value: 'Display', label: 'Display (Anton)' },
  { value: 'Body', label: 'Body (Inter)' },
  { value: 'Serif', label: 'Serif (Playfair)' },
]

// Google family names are title-cased ("Playfair Display"); try the raw input
// first so exact names (incl. "DM Serif Display") always work, then a
// title-cased retry rescues lowercase typing.
function titleCase(s: string): string {
  return s.replace(/\S+/g, w => (w[0] ?? '').toUpperCase() + w.slice(1).toLowerCase())
}

function OptionRow({
  family,
  label,
  selected,
  onPick,
  previewFace,
}: {
  family: string
  label?: string
  selected: boolean
  onPick: (family: string) => void
  previewFace?: string
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(family)}
      className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm transition-colors ${
        selected ? 'text-paper' : 'text-paper-dim hover:bg-bg-3 hover:text-paper'
      }`}
    >
      <span style={{ fontFamily: `"${previewFace ?? family}"`, fontSynthesis: 'none' }}>
        {label ?? family}
      </span>
      {selected && <Check className="h-3.5 w-3.5 shrink-0 text-acid" />}
    </button>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-2 pb-0.5 text-[10px] font-medium tracking-wide text-paper-faint uppercase">
      {children}
    </div>
  )
}

export function FontPicker({
  value,
  brandFamilies,
  onChange,
}: {
  value: string
  /** The brand kit's REAL recorded families (already loadable), pinned first. */
  brandFamilies: string[]
  onChange: (family: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [probing, setProbing] = useState(false)
  const [notFound, setNotFound] = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  // Load the visible faces once the menu opens so options preview in themselves
  // (parallel, cached app-globally; failures just show the fallback face).
  useEffect(() => {
    if (!open) return
    for (const fam of [...brandFamilies, ...CURATED_FAMILIES]) void probeFamily(fam)
  }, [open, brandFamilies])

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  const pick = (family: string) => {
    onChange(family)
    setOpen(false)
    setQuery('')
    setNotFound(null)
  }

  const q = query.trim().toLowerCase()
  const brand = brandFamilies.filter(f => !q || f.toLowerCase().includes(q))
  const curated = CURATED_FAMILIES.filter(
    f => !brandFamilies.includes(f) && (!q || f.toLowerCase().includes(q))
  )
  const builtin = BUILTIN.filter(o => !q || o.label.toLowerCase().includes(q))
  const exactListed =
    !q ||
    [...brandFamilies, ...CURATED_FAMILIES].some(f => f.toLowerCase() === q) ||
    LOGICAL_FONT_FAMILIES.has(query.trim())

  const tryManual = async () => {
    const typed = query.trim()
    if (!typed || probing) return
    setProbing(true)
    setNotFound(null)
    const candidates = [typed, titleCase(typed)]
    for (const fam of candidates) {
      if (await probeFamily(fam)) {
        setProbing(false)
        pick(fam)
        return
      }
    }
    setProbing(false)
    setNotFound(typed)
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex w-full items-center justify-between rounded-md border border-line bg-bg-3 px-2.5 py-2 text-sm text-paper transition-colors hover:border-line-bright"
      >
        <span style={{ fontFamily: `"${value}"`, fontSynthesis: 'none' }}>
          {BUILTIN.find(o => o.value === value)?.label ?? value}
        </span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-paper-faint" />
      </button>
      {open && (
        <div className="absolute top-full right-0 left-0 z-40 mt-1 max-h-80 overflow-y-auto rounded-xl border border-line bg-bg-2 pb-1 shadow-[var(--shadow-card)]">
          <div className="sticky top-0 border-b border-line bg-bg-2 p-2">
            <div className="flex items-center gap-1.5 rounded-md border border-line bg-bg-3 px-2 py-1.5">
              <Search className="h-3.5 w-3.5 shrink-0 text-paper-faint" />
              <input
                value={query}
                onChange={e => {
                  setQuery(e.target.value)
                  setNotFound(null)
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') void tryManual()
                }}
                placeholder="Search or type any Google Font…"
                className="w-full bg-transparent text-sm text-paper placeholder:text-paper-faint focus:outline-none"
              />
              {probing && (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-paper-faint" />
              )}
            </div>
            {notFound && (
              <div className="px-1 pt-1 text-[11px] text-red-400">
                “{notFound}” isn’t on Google Fonts.
              </div>
            )}
          </div>
          {brand.length > 0 && (
            <>
              <SectionLabel>Brand</SectionLabel>
              {brand.map(f => (
                <OptionRow key={f} family={f} selected={value === f} onPick={pick} />
              ))}
            </>
          )}
          {curated.length > 0 && (
            <>
              <SectionLabel>Curated</SectionLabel>
              {curated.map(f => (
                <OptionRow key={f} family={f} selected={value === f} onPick={pick} />
              ))}
            </>
          )}
          {builtin.length > 0 && (
            <>
              <SectionLabel>Built-in</SectionLabel>
              {builtin.map(o => (
                <OptionRow
                  key={o.value}
                  family={o.value}
                  label={o.label}
                  selected={value === o.value}
                  onPick={pick}
                />
              ))}
            </>
          )}
          {q && !exactListed && (
            <button
              type="button"
              onClick={() => void tryManual()}
              disabled={probing}
              className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-sm text-paper-dim hover:bg-bg-3 hover:text-paper disabled:opacity-50"
            >
              {probing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Search className="h-3.5 w-3.5" />
              )}
              Load “{query.trim()}” from Google Fonts
            </button>
          )}
        </div>
      )}
    </div>
  )
}
